import { AxiosError, AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';
import userClient from './userClient';
import client from 'api/client';
import { systemId } from 'src/app.config';
import { OpenIdResponse, IUser, LoginResponse } from 'types/loginResponse';
import { SystemClaimPermission } from 'src/types/claims';
import setAuthorizationToken from 'api/setAuthorizationToken';
import { SnackbarUtils } from 'src/utils/snackbarUtilsConfigurator';

export async function Me(): Promise<IUser | undefined> {
  try {
    const user = await client.get<IUser>('/Login/Me');
    if (user.status) return user.data;
    return undefined;
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.data) return err.response.data as IUser;
    return undefined;
  }
}
export interface LoginProps {
  username: string;
  password: string;
}

export async function Login(loginProps: LoginProps) {
  try {
    const response = await requestLogin(loginProps);
    if (response.status !== 200)
      return 'Ha ocurrido un error en la identificación.';

    const { access_token, refresh_token } = response.data;
    localStorage.setItem('AccessToken', access_token);
    localStorage.setItem('RefreshToken', refresh_token);

    const exchangeResponse = await requestTokenExchange(access_token);
    const systemAccessToken = exchangeResponse?.data?.access_token;
    localStorage.setItem('PermissionToken', systemAccessToken);

    const decodedToken = jwtDecode<any>(systemAccessToken);
    let parsedClaims: SystemClaimPermission = {} as SystemClaimPermission;

    parsedClaims =
      typeof decodedToken.claims === 'string'
        ? JSON.parse(decodedToken.claims)
        : decodedToken.claims;

    const userPayload = {
      userName: decodedToken.username,
      name: decodedToken.name,
      email: decodedToken.email,
      rut: decodedToken.rut,
      rol: decodedToken.rol,
      profileName: decodedToken.profileName,
      claims: parsedClaims,
    };

    setAuthorizationToken();

    return {
      authorization: systemAccessToken,
      user: userPayload,
      succeeded: true,
    } as LoginResponse;
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.data) return err.response.data as string;
    return 'Ha ocurrido un error en la autentificación.';
  }
}

export async function refreshToken() {
  try {
    //get refresh token from local storage
    const refreshToken = localStorage.getItem('RefreshToken');
    if (!refreshToken) throw new Error('No refresh token found');
    //set new access token and refresh token
    const response = await resquestRefreshToken(refreshToken);
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('AccessToken', access_token);
    localStorage.setItem('RefreshToken', refresh_token);
    //exchange new access token for permission token and store it in local storage
    const exchangeResponse = await requestTokenExchange(access_token);
    const systemAccessToken = exchangeResponse?.data?.access_token;
    localStorage.setItem('PermissionToken', systemAccessToken);
    return true;
  } catch {
    return false;
  }
}

export const requestLogin = (loginProps: LoginProps) => {
  const payload = new URLSearchParams();
  payload.append('grant_type', 'password');
  payload.append('username', loginProps.username);
  payload.append('password', loginProps.password);
  payload.append('scope', 'offline_access');

  return userClient.post<OpenIdResponse>('/api/Connect/Token', payload, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: true,
  });
};

export const requestTokenExchange = (AuthToken: string) => {
  const payload = new URLSearchParams();
  payload.append(
    'grant_type',
    'urn:ietf:params:oauth:grant-type:token-exchange'
  );
  payload.append('subject_token', AuthToken);
  payload.append(
    'subject_token_type',
    'urn:ietf:params:oauth:token-type:access_token'
  );
  payload.append('system_id', systemId);

  return userClient.post<OpenIdResponse>('/api/Connect/Token', payload, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: true,
  });
};

async function resquestRefreshToken(refreshToken: string) {
  const payload = new URLSearchParams();
  payload.append('grant_type', 'refresh_token');
  payload.append('refresh_token', refreshToken);
  payload.append('scope', 'offline_access');
  return userClient.post<OpenIdResponse>('/api/Connect/Token', payload, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    withCredentials: true,
  });
}

export interface ChangePasswordProps {
  userName: string;
  currentPassword: string;
  newPassword: string;
}

export interface ChangeEmailProps {
  UserName: string;
  NewEmail: string;
  Rut: string;
}

export async function ChangePassword(
  changeProps: ChangePasswordProps
): Promise<{ ok: boolean; message: string }> {
  try {
    const response = await userClient.put<string>(
      '/api/User/ChangePassword',
      changeProps
    );
    switch (response.status) {
      case 200:
        SnackbarUtils.success('Constraseña actualizada correctamente');
        return {
          ok: true,
          message: 'Se ha modificado correctamente la contraseña.',
        };
      case 400:
        return { ok: false, message: 'Contraseña incorrecta..' };
      default:
        return { ok: false, message: 'Ha ocurrido un error.' };
    }
  } catch (error) {
    const err = error as AxiosError;
    if (err.status === 400)
      return { ok: false, message: 'Contraseña incorrecta.' };
    return {
      ok: false,
      message: 'Ha ocurrido un error al cambiar la contraseña.',
    };
  }
}

export async function ChangeEmail(
  changeProps: ChangeEmailProps
): Promise<{ ok: boolean; message: string; profileName?: string }> {
  try {
    const responseUser = await userClient.put<{
      message: string;
      profileName: string;
    }>('/api/User/ChangeEmail', changeProps);

    switch (responseUser.status) {
      case 200:
        return {
          ok: true,
          message: responseUser.data.message,
          profileName: responseUser.data.profileName,
        };
      case 400:
        return { ok: false, message: 'Usuario incorrecto.' };
      default:
        return { ok: false, message: 'Ha ocurrido un error.' };
    }
  } catch (error) {
    const err = error as AxiosError;
    if (err.response?.status === 400)
      return { ok: false, message: 'Usuario incorrecto.' };
    return {
      ok: false,
      message: 'Ha ocurrido un error al cambiar el correo.',
    };
  }
}

export const resetPassword = (userName: string): Promise<AxiosResponse<void>> =>
  userClient.put(
    '/api/User/ResetPasswordUser',
    {},
    {
      params: {
        userName,
      },
    }
  );

export interface LogoutProps {
  authorization: string;
  version: string;
}

export const Logout = ({ authorization, version }: LogoutProps) => {
  return userClient.get('/api/ConnectionLog/RegisterLogout', {
    headers: {
      Authorization: `Bearer ${authorization}`,
    },
    params: {
      systemId,
      version,
    },
    withCredentials: true,
  });
};
